import React, { useState } from 'react';
import {
  Shield,
  FileText,
  Activity,
  Lock,
  Scale,
  Brain,
  Wrench,
  Wifi,
  Package,
  ArrowRight,
  CheckCircle,
  Mail,
  ClipboardList,
} from 'lucide-react';

// ─── Data ────────────────────────────────────────────────────────────────────

const serviceCategories = [
  {
    category: 'Cybersecurity & Compliance',
    description: 'Assessment, monitoring, and compliance services built for small businesses — delivered with federal-grade expertise.',
    services: [
      {
        icon: Shield,
        name: 'CyvaCheck',
        tagline: 'Free 20-minute live security check',
        desc: 'A free 20-minute live security check on your business domain. We show you exactly where you are exposed — email security, credential leaks, and more. No obligation, no jargon, just answers.',
        highlights: ['Email security analysis', 'Domain credential leak check', 'Live results in 20 minutes', 'No commitment required'],
        price: 'Free',
        priceNote: 'Always free',
        highlight: true,
        cta: 'Book Your CyvaCheck',
      },
      {
        icon: FileText,
        name: 'CyvaScore',
        tagline: 'Full security health assessment',
        desc: 'A complete security health assessment for your business. We run five automated checks, conduct a structured intake review, and deliver a plain-English report within 48 hours — with prioritized findings, exact fix steps, and your compliance exposure mapped out.',
        highlights: ['Five automated security checks', 'Structured intake review', 'Plain-English report in 48 hours', 'Compliance exposure mapped'],
        price: 'Custom assessment',
        priceNote: 'One-time engagement',
        highlight: false,
        cta: 'Request a CyvaScore',
      },
      {
        icon: Activity,
        name: 'CyvaGuard Essentials',
        tagline: 'Monthly automated security monitoring',
        desc: 'Monthly automated monitoring that keeps you protected as threats evolve. Includes monthly security score tracking, AI-generated phishing simulations for your staff, dark web credential monitoring, and a one-page executive summary every month.',
        highlights: ['Monthly security score tracking', 'AI phishing simulations for staff', 'Dark web credential monitoring', 'Monthly executive summary'],
        price: 'Monthly retainer',
        priceNote: 'Ongoing protection',
        highlight: false,
        cta: 'Get Started',
      },
      {
        icon: Lock,
        name: 'CyvaGuard Pro',
        tagline: 'Advanced monitoring with advisory support',
        desc: 'Everything in Essentials plus quarterly re-assessments, vendor risk reviews, AI-drafted security policy templates, and a dedicated channel for security questions answered within 24 hours. Built for practices and firms that need ongoing advisory support.',
        highlights: ['Everything in Essentials', 'Quarterly re-assessments', 'Vendor risk reviews', 'Security questions answered in 24hrs'],
        price: 'Monthly retainer',
        priceNote: 'Ongoing protection',
        highlight: false,
        cta: 'Get Started',
      },
      {
        icon: Scale,
        name: 'CyvaComply',
        tagline: 'Structured compliance gap closure',
        desc: 'Structured compliance gap closure for businesses facing a specific regulatory deadline. We map your current controls against HIPAA, CMMC, SOC 2, or PCI-DSS, identify every gap, and deliver all required policy documents and evidence templates — reviewed and signed off by a federal cybersecurity veteran.',
        highlights: ['HIPAA · CMMC · SOC 2 · PCI-DSS mapping', 'Full gap analysis', 'Policy documents and evidence templates', 'Signed off by federal cybersecurity veteran'],
        price: 'Project-based',
        priceNote: 'Scoped per engagement',
        highlight: false,
        cta: 'Discuss Your Requirements',
      },
      {
        icon: Brain,
        name: 'CyvaAware',
        tagline: 'AI threat awareness staff workshop',
        desc: 'A one-hour staff workshop on AI-powered threats — phishing emails, deepfake voice scams, and bot fraud. Built around real local incidents including the Baltimore City Community College AI bot attack. Delivered in-person or remotely. Includes a policy document your team keeps.',
        highlights: ['Real Baltimore incident case studies', 'Phishing, deepfakes, and bot fraud', 'In-person or remote delivery', 'Includes a take-home policy document'],
        price: 'Per session',
        priceNote: 'Group or individual',
        highlight: false,
        cta: 'Book a Workshop',
      },
      {
        icon: ClipboardList,
        name: 'CMMC Compliance Readiness',
        tagline: 'CMMC self-assessment requirements are in effect now for Maryland defense subcontractors',
        desc: "CMMC compliance is a live contract requirement for defense subcontractors. While the DoD suspended its Phase 2 third-party assessment deadline in July 2026, self-assessments are required now. We assess your posture against NIST SP 800-171 controls, calculate your SPRS score, build your System Security Plan, and create a remediation roadmap.",
        highlights: [
          'NIST SP 800-171 controls assessment',
          'SPRS score calculation',
          'System Security Plan (SSP) development',
          'Remediation roadmap with prioritized steps',
        ],
        price: 'Project-based',
        priceNote: 'Scoped per engagement',
        highlight: false,
        cta: 'Start Your CMMC Readiness',
      },
    ],
  },
  {
    category: 'IT Consulting',
    description: 'Hands-on IT support and infrastructure management — from day-to-day helpdesk to full network management.',
    services: [
      {
        icon: Wrench,
        name: 'IT Setup and Support',
        tagline: 'Hourly IT support, no contract required',
        desc: 'Hands-on IT support for small businesses — device setup, troubleshooting, software configuration, and day-to-day technical help when you need it. No contract required.',
        highlights: ['Device setup and configuration', 'Software troubleshooting', 'Day-to-day technical help', 'No long-term contract required'],
        price: 'Hourly',
        priceNote: 'As needed',
        highlight: false,
        cta: 'Get Support',
      },
      {
        icon: Wifi,
        name: 'Network Management',
        tagline: 'Professional network configuration and monitoring',
        desc: 'Professional network configuration and ongoing management for your office. We set it up right, keep it running, and make sure it stays secure — so you can focus on your business.',
        highlights: ['Network design and configuration', 'Ongoing monitoring and maintenance', 'Security hardening built in', 'Single point of contact'],
        price: 'Monthly retainer',
        priceNote: 'Ongoing management',
        highlight: false,
        cta: 'Get Started',
      },
      {
        icon: Package,
        name: 'Combined IT & Security Package',
        tagline: 'One vendor for IT support and security monitoring',
        desc: 'Monthly IT support combined with CyvaGuard security monitoring — our most comprehensive offering for businesses that want one trusted vendor for everything. One bill, one point of contact, complete coverage.',
        highlights: ['Full IT support included', 'CyvaGuard security monitoring', 'One bill, one contact', 'Best value for complete coverage'],
        price: 'Monthly retainer',
        priceNote: 'Most popular',
        highlight: false,
        cta: 'Get a Custom Quote',
      },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Services: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <div className="w-full">

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="bg-[#001a4d] py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-5">
            <Shield size={14} className="text-[#0B8A8F]" />
            <span className="text-white/80 text-xs font-bold uppercase tracking-widest">What We Offer</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
            Services built for Baltimore small businesses
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
            Enterprise-quality cybersecurity and IT support — at prices a dental practice,
            law firm, or small business can actually afford.
          </p>
        </div>
      </section>

      {/* ── CATEGORY TABS ───────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 sticky top-[73px] z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-3">
            {serviceCategories.map((cat, i) => (
              <button
                key={i}
                onClick={() => setActiveCategory(i)}
                className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  activeCategory === i
                    ? 'bg-[#001a4d] text-white'
                    : 'text-gray-500 hover:text-[#001a4d] hover:bg-gray-50'
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── SERVICE CARDS ───────────────────────────────────── */}
      {serviceCategories.map((cat, ci) => (
        <section
          key={ci}
          className={`py-16 px-4 sm:px-6 lg:px-8 ${activeCategory !== ci ? 'hidden' : ''} ${ci % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
        >
          <div className="max-w-6xl mx-auto">

            {/* Category intro */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-black uppercase tracking-widest text-[#0B8A8F]">{cat.category}</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <p className="text-gray-500 text-sm max-w-xl">{cat.description}</p>
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cat.services.map((svc, i) => {
                const Icon = svc.icon;
                return (
                  <div
                    key={i}
                    className={`flex flex-col rounded-2xl border p-6 transition-all hover:shadow-lg ${
                      svc.highlight
                        ? 'bg-[#0B8A8F] border-[#0B8A8F]'
                        : 'bg-white border-gray-200 hover:border-[#0B8A8F]/30'
                    }`}
                  >
                    {/* Icon + name */}
                    <div className="mb-4">
                      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3 ${
                        svc.highlight ? 'bg-white/20' : 'bg-[#001a4d]'
                      }`}>
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                      </div>
                      <h3 className={`font-extrabold text-lg leading-tight mb-0.5 ${svc.highlight ? 'text-white' : 'text-[#001a4d]'}`}>
                        {svc.name}
                      </h3>
                      <p className={`text-xs font-semibold ${svc.highlight ? 'text-white/70' : 'text-[#0B8A8F]'}`}>
                        {svc.tagline}
                      </p>
                    </div>

                    {/* Description */}
                    <p className={`text-sm leading-relaxed mb-5 ${svc.highlight ? 'text-white/85' : 'text-gray-500'}`}>
                      {svc.desc}
                    </p>

                    {/* Highlights */}
                    <ul className="space-y-2 mb-6 flex-1">
                      {svc.highlights.map((h, hi) => (
                        <li key={hi} className="flex items-start gap-2">
                          <CheckCircle
                            size={14}
                            className={`mt-0.5 flex-shrink-0 ${svc.highlight ? 'text-white/80' : 'text-[#0B8A8F]'}`}
                            strokeWidth={2.5}
                          />
                          <span className={`text-xs leading-relaxed ${svc.highlight ? 'text-white/80' : 'text-gray-600'}`}>
                            {h}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Footer: price + CTA */}
                    <div className={`flex items-center justify-between pt-4 border-t ${
                      svc.highlight ? 'border-white/20' : 'border-gray-100'
                    }`}>
                      <div>
                        <div className={`text-base font-bold ${svc.highlight ? 'text-white' : 'text-[#001a4d]'}`}>
                          {svc.price}
                        </div>
                        <div className={`text-xs ${svc.highlight ? 'text-white/60' : 'text-gray-400'}`}>
                          {svc.priceNote}
                        </div>
                      </div>
                      <a
                        href={`mailto:info@cyvantixsolutions.com?subject=${encodeURIComponent(svc.cta + ' — ' + svc.name)}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-all ${
                          svc.highlight
                            ? 'bg-white text-[#0B8A8F] hover:bg-gray-50'
                            : 'bg-[#001a4d] text-white hover:bg-[#002a6d]'
                        }`}
                      >
                        {svc.cta}
                        <ArrowRight size={12} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* ── QUOTE LINE ──────────────────────────────────────── */}
      <div className="bg-gray-50 border-t border-gray-100 py-6 px-4 text-center">
        <p className="text-gray-500 text-sm">
          Not sure which service is right for you?{' '}
          <a href="mailto:info@cyvantixsolutions.com?subject=Service%20Inquiry" className="text-[#0B8A8F] font-semibold hover:underline">
            Email us
          </a>{' '}
          and we'll recommend the right starting point for your business size and needs.
        </p>
      </div>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="bg-[#001a4d] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Start with a free CyvaCheck
          </h2>
          <p className="text-white/70 text-base mb-8 leading-relaxed">
            Not sure where to begin? Book a free 20-minute security check on your domain.
            No obligation, no jargon — just a clear picture of where you stand.
          </p>
          <a
            href="mailto:info@cyvantixsolutions.com?subject=Book%20Free%20CyvaCheck"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B8A8F] text-white font-bold rounded-xl hover:bg-[#097a7f] transition-all shadow-lg text-base mb-8"
          >
            Book Your Free CyvaCheck
            <ArrowRight size={18} />
          </a>
          <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
            <Mail size={14} />
            <a href="mailto:info@cyvantixsolutions.com" className="hover:text-white transition-colors">
              info@cyvantixsolutions.com
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Services;
